import PropTypes from 'prop-types'
import PublicNav from './PublicNav'
import Footer from './Footer'

const AboutPage = ({ onBack, onLogin, onShowFeatures, onShowPricing, onShowContact, onShowPrivacy, onShowTerms }) => {
  return (
    <div className="min-h-screen bg-gray-950">
      <PublicNav 
        onShowFeatures={onShowFeatures}
        onShowPricing={onShowPricing}
        onLogin={onLogin}
        onBack={onBack}
        activeProduct={false}
        activePricing={false}
        activeHome={false}
      />

      <div className="relative py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="font-display text-5xl font-light tracking-tight text-white sm:text-7xl mb-6">
              About <span className="text-sky-300">Opptora</span>
            </h1>
            <p className="mx-auto max-w-3xl text-2xl/10 text-gray-300">
              We're building the future of idea validation with AI-powered market research and insights.
            </p>
          </div>

          {/* Who We Are Section */}
          <div className="mb-24">
            <div className="rounded-2xl bg-gradient-to-br from-sky-800/40 via-sky-700/30 to-sky-900/40 shadow-none outline -outline-offset-1 outline-sky-400/40 px-8 py-12 sm:px-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-sky-500/10 via-transparent to-transparent rounded-2xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <h2 className="font-display text-3xl font-light tracking-tight text-white mb-6">
                  Who We Are
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Opptora is the first product from <span className="text-sky-300 font-semibold">Levantar AI Limited</span>, an 
                  AI-first development consultancy dedicated to making AI accessible, secure, and intuitive for everyone.
                </p>
                <p className="text-xl text-gray-300 leading-relaxed">
                  At Levantar AI, our mission is to help everyone access AI in secure, non-technical, and intuitive yet 
                  structured ways. We combine modern software development practices with cutting-edge AI to build products 
                  that solve real problems—starting with Opptora.
                </p>
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="mb-24">
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-8 py-12 sm:px-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-sky-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <h2 className="font-display text-3xl font-light tracking-tight text-white mb-6">
                  Why Opptora?
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Every year, countless startups fail because they build products nobody wants. Traditional validation 
                  requires expensive specialists—Market Researchers, Business Analysts, UX Designers, and Technical 
                  Architects—costing hundreds of thousands annually.
                </p>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Opptora democratizes access to world-class business validation expertise through AI, making it affordable 
                  and accessible to everyone with an idea worth pursuing.
                </p>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="mb-24">
            <div className="rounded-2xl bg-gradient-to-br from-blue-900/30 to-blue-800/20 shadow-none outline -outline-offset-1 outline-blue-500/30 px-8 py-12 sm:px-12 relative overflow-hidden text-center">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/10 via-transparent to-transparent rounded-2xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <h2 className="font-display text-3xl font-light tracking-tight text-white mb-4">
                  Currently in Private Beta
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                  We're working with a select group of early users to refine our platform and ensure 
                  we're delivering maximum value. Interested in joining?
                </p>
                <button 
                  onClick={() => onLogin('register')}
                  className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-8 py-3.5 text-lg font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 transition-all shadow-lg"
                >
                  Request Access
                </button>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mb-24">
            <h2 className="font-display text-3xl font-light tracking-tight text-white text-center mb-12">
              Our Values
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {[
                {
                  title: 'Accessible Innovation',
                  description: 'World-class business validation shouldn\'t cost a fortune. We make expert-level insights available to everyone.',
                },
                {
                  title: 'Data-Driven Decisions',
                  description: 'Move beyond gut feelings. Our AI-powered analysis gives you the hard data you need to make confident choices.',
                },
                {
                  title: 'Speed & Agility',
                  description: 'Get insights in hours, not weeks. Validate faster, pivot smarter, and stay ahead of the market.',
                },
                {
                  title: 'Transparency',
                  description: 'No black boxes. We show you our reasoning and methodology so you understand the "why" behind every recommendation.',
                },
              ].map((value, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-800/30 to-gray-800/30 shadow-none outline -outline-offset-1 outline-white/5 p-6">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                  <h3 className="text-lg font-semibold text-white mb-2 relative z-10">{value.title}</h3>
                  <p className="text-base text-gray-400 relative z-10">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-8 py-12 sm:px-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-64 h-32 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <h2 className="font-display text-3xl font-light tracking-tight text-white mb-4">
                  Ready to validate your idea?
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  Join the growing community of entrepreneurs using AI to turn their ideas into successful businesses.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => onLogin('register')}
                    className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-8 py-3.5 text-lg font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 transition-all shadow-lg"
                  >
                    Get Started
                  </button>
                  <button 
                    onClick={onShowFeatures}
                    className="rounded-md bg-gray-900 px-8 py-3.5 text-lg font-semibold text-white hover:bg-gray-800 transition-all"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer variant="public" onShowFeatures={onShowFeatures} onShowPricing={onShowPricing} onShowContact={onShowContact} onShowPrivacy={onShowPrivacy} onShowTerms={onShowTerms} />
    </div>
  )
}

AboutPage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onShowFeatures: PropTypes.func.isRequired,
  onShowPricing: PropTypes.func.isRequired,
  onShowContact: PropTypes.func,
  onShowPrivacy: PropTypes.func,
  onShowTerms: PropTypes.func,
}

export default AboutPage
